if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.timesactObserver = new MutationObserver(() => this.reconcileTimesactState());
      }

      connectedCallback() {
        this.timesactObserver.observe(this, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });

        this.reconcileTimesactState();
      }

      disconnectedCallback() {
        this.timesactObserver.disconnect();
      }

      reconcileTimesactState() {
        const element = this.querySelector('[data-sova-timesact-form-state]');
        if (!element) return;

        const state = JSON.parse(element.textContent);

        if (state.purchasable !== 'true' && state.purchasable !== true) {
          if (!this.submitButton.disabled) {
            this.submitButton.disabled = true;
          }

          if (this.submitButton.getAttribute('aria-disabled') !== 'true') {
            this.submitButton.setAttribute('aria-disabled', 'true');
          }

          if (
            state.normalLabel &&
            this.submitButtonText.textContent.trim() !== state.normalLabel
          ) {
            this.submitButtonText.textContent = state.normalLabel;
          }
        }

        if (state.active) return;

        this.form
          .querySelectorAll('[data-timesact-property], [data-timesact-tracking]')
          .forEach((input) => input.remove());

        if (state.purchasable === 'true' || state.purchasable === true) {
          this.form
            .querySelectorAll('.timesact-backInStock-button')
            .forEach((button) => button.remove());
        }

        if (
          !this.error &&
          !this.submitButton.classList.contains('loading') &&
          state.normalLabel &&
          this.submitButtonText.textContent.trim() !== state.normalLabel
        ) {
          this.submitButtonText.textContent = state.normalLabel;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.reconcileTimesactState();

        if (
          this.submitButton.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        const stateElement = this.querySelector('[data-sova-timesact-form-state]');
        const state = stateElement ? JSON.parse(stateElement.textContent) : null;

        if (
          state &&
          state.purchasable !== 'true' &&
          state.purchasable !== true
        ) {
          return;
        }

        const timesactData = new FormData(this.form);

        if (state?.active) {
          const marker = timesactData.get('properties[_timesact_preorder]');
          const hasMarker =
            marker !== null &&
            marker !== '' &&
            marker !== 'false';

          const hasLabel =
            state.labelKey &&
            state.labelValue &&
            timesactData.get(`properties[${state.labelKey}]`) === state.labelValue;

          const hasPlan =
            state.sellingPlanId &&
            String(timesactData.get('selling_plan')) ===
              String(state.sellingPlanId).split('/').pop();

          if (!hasMarker && !hasLabel && !hasPlan) {
            this.handleErrorMessage(state.error);
            return;
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker('add:wait-for-subscribers');

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.querySelector('.loading__spinner').classList.add('hidden');
            this.reconcileTimesactState();
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const element = this.querySelector('[data-sova-timesact-form-state]');
        const state = element ? JSON.parse(element.textContent) : null;

        if (
          state &&
          state.purchasable !== 'true' &&
          state.purchasable !== true
        ) {
          disable = true;
          text = state.normalLabel || text;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');

          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          if (!this.submitButton.classList.contains('loading') && !this.error) {
            this.submitButton.removeAttribute('aria-disabled');
          }

          this.submitButtonText.textContent =
            text || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}